import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import { ChipInput, DropdownInput } from '@trueprodigy/components';
import TextInput from '@components/TextInputCaseSetting';
import { withStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { compose, lifecycle, withHandlers } from 'recompose';
import { Field, reduxForm } from 'redux-form/immutable';
import { createStructuredSelector } from 'reselect';
import ImmutablePropTypes from 'react-immutable-proptypes';
import styled from 'styled-components';
import PidImportModal from '@modules/appraisal/components/PidImportModal';

import { RIGHTS } from '@constants';
import { eventFormListSelector } from '@modules/events/selectors/eventform';
import {
  eventDataSelector,
  eventTagsDataSelector,
  eventObjectsDataSelector,
} from '@modules/events/selectors/event';
import * as eventActions from '@modules/events/actions/event';
import { userDataSelector, userStatusSelector } from '@selectors/global';
import { hasRight } from '@utils';

import {
  getEventForm,
  SelectWrapper,
  formatEventData,
  renderFormFields,
} from './EventDetailFormHelper';
import ImportIcon from '../../../../../assets/images/list-ol-regular.svg';

const customFilter = (option, input) => {
  return option.label.toLowerCase().includes(input.toLowerCase());
};

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'white',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
  popper: {},
}))(Tooltip);

const EventDetailForm = ({
  eventID,
  formValues,
  handleSubmit,
  hasEventRight,
  eventFormList,
  onChangeTagsObjects,
  pristine,
  submitting,
  onClearForm,
  onDuplicate,
  onFreshCreate,
  hideCreateButtons,
  objectIDReadOnly,
  change,
  userStatus,
  saveButtonText,
}) => {
  const curEventForm = getEventForm(eventFormList, formValues.formID);
  const objectIDList = formValues.objectIDList || [];
  const [open, setOpen] = useState(false);
  const isReadOnly = userStatus.get('readOnly');

  const getClearList = (originalData, newData) => {
    return originalData.concat(
      newData.filter(
        id => !originalData.contains(Number(id)) && !originalData.contains(id),
      ),
    );
  };

  return (
    <EventDetailForm.Wrapper onSubmit={handleSubmit}>
      {!eventID && (
        <EventDetailForm.Row items={2}>
          <SelectWrapper>
            <Field
              component={DropdownInput}
              label="Event Form"
              name="formID"
              normalize={value => value.value}
              options={
                eventFormList.get('data')
                  ? eventFormList
                      .get('data')
                      .toJS()
                      .filter(value => value.categoryEnabled)
                      .map(value => ({
                        value: value.formID,
                        label: value.formName,
                      }))
                  : []
              }
              customFilter={customFilter}
            />
          </SelectWrapper>
        </EventDetailForm.Row>
      )}
      {formValues.formID && (
        <React.Fragment>
          {!eventID && (
            <React.Fragment>
              <EventDetailForm.Row items={1}>
                <EventDetailForm.DisabledField
                  component={TextInput}
                  disabled
                  label="Description"
                  name="description"
                  type="text"
                />
              </EventDetailForm.Row>
              <EventDetailForm.Row items={2}>
                <EventDetailForm.DisabledField
                  component={TextInput}
                  disabled
                  label="Data Object"
                  name="dataObject"
                  type="text"
                />
                <EventDetailForm.DisabledField
                  component={TextInput}
                  disabled
                  label="Event Group"
                  name="formGroup"
                  type="text"
                />
              </EventDetailForm.Row>
            </React.Fragment>
          )}
          <EventDetailForm.Row items={2}>
            <SelectWrapper>
              <Field
                component={DropdownInput}
                disabled={isReadOnly || !hasEventRight()}
                label="Event Type"
                name="eventType"
                normalize={value => value.value}
                options={
                  curEventForm?.eventTypes
                    ? curEventForm.eventTypes.map(value => ({
                        value,
                        label: value,
                      }))
                    : []
                }
              />
            </SelectWrapper>
            <Tooltip title="Description">
              <Field
                component={TextInput}
                disabled={isReadOnly || !hasEventRight()}
                multiline={true}
                label="Event Description"
                name="eventDescription"
                type="text"
                maxRows={3}
              />
            </Tooltip>
          </EventDetailForm.Row>
          <EventDetailForm.Row items={2}>
            <SelectWrapper style={{ display: 'flex', alignItems: 'center' }}>
              <Field
                component={ChipInput}
                disabled={!hasEventRight() || objectIDReadOnly || isReadOnly}
                label="Object ID"
                name="objectIDList"
                type="number"
                onChange={(ev, val) => onChangeTagsObjects(null, val || [])}
              />
              <HtmlTooltip
                classes={{ popper: 'custom-arrow-tooltip' }}
                title={
                  <div className="tooltip-content">
                    Object ID List
                    <div className="arrow" />
                  </div>
                }
              >
                <i onClick={() => setOpen(true)}>
                  <img src={ImportIcon} />
                </i>
              </HtmlTooltip>
            </SelectWrapper>
            <SelectWrapper>
              <Field
                component={ChipInput}
                disabled={isReadOnly || !hasEventRight()}
                label="Tags"
                name="tags"
                onChange={(ev, val) => onChangeTagsObjects(val || [], null)}
              />
            </SelectWrapper>
          </EventDetailForm.Row>
          <EventDetailForm.SectionSplitter />
          <EventDetailForm.FieldsRow items={2}>
            {curEventForm?.formFields &&
              renderFormFields(curEventForm.formFields)}
          </EventDetailForm.FieldsRow>
          <EventDetailForm.SectionSplitter />
          <EventDetailForm.Actions>
            {!eventID && (
              <Button
                disabled={submitting || isReadOnly ||  !hasEventRight()}
                variant="contained"
                onClick={onClearForm}
              >
                Cancel
              </Button>
            )}
            <Button
              color="primary"
              disabled={pristine || submitting || isReadOnly ||  !hasEventRight()}
              type="submit"
              variant="contained"
            >
              {saveButtonText ? saveButtonText : 'Save'}
            </Button>
            {eventID && !hideCreateButtons && (
              <Tooltip title="Create New Event - with Persist Fields">
                <Button
                  color="primary"
                  disabled={isReadOnly || !hasEventRight()}
                  variant="contained"
                  onClick={onDuplicate}
                >
                  Create New Event
                </Button>
              </Tooltip>
            )}
            {eventID && !hideCreateButtons && (
              <Tooltip title="Create New Event - No Persist Fields">
                <Fab
                  aria-label="Create New Event"
                  color="primary"
                  size="small"
                  onClick={onFreshCreate}
                >
                  <AddIcon />
                </Fab>
              </Tooltip>
            )}
          </EventDetailForm.Actions>
        </React.Fragment>
      )}
      <PidImportModal
        open={open}
        title="Object "
        handleClose={() => {
          setOpen(false);
        }}
        onSubmit={(pids = []) => {
          const list = getClearList(
            objectIDList || [],
            pids.filter(i => !!i) || [],
          );
          change('objectIDList', list);
          onChangeTagsObjects(null, list);
          setOpen(false);
        }}
      />
    </EventDetailForm.Wrapper>
  );
};

EventDetailForm.propTypes = {
  eventFormList: ImmutablePropTypes.map,
  eventID: PropTypes.string,
  formValues: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  hasEventRight: PropTypes.func.isRequired,
  hideCreateButtons: PropTypes.bool,
  initialize: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  objectIDReadOnly: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  onChangeTagsObjects: PropTypes.func.isRequired,
  onClearForm: PropTypes.func.isRequired,
  onCreateRedirect: PropTypes.func,
  onCreateSuccess: PropTypes.func,
  onDuplicate: PropTypes.func.isRequired,
  onFreshCreate: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  userStatus: PropTypes.any,
  saveButtonText: PropTypes.string,
};

EventDetailForm.Wrapper = styled.form`
  background: white;
  min-height: 350px;
  padding: 15px;
  border-top: 4px solid #acb8cc;
`;

EventDetailForm.Row = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-end;

  & > div {
    width: ${props => `${100 / props.items}%`};
    padding: 2px 5px !important;
  }
`;

EventDetailForm.FieldsRow = styled(EventDetailForm.Row)`
  flex-wrap: wrap;
  align-items: flex-start !important;
`;

EventDetailForm.SectionSplitter = styled.div`
  height: 2px;
  background-color: #acb8cc;
  margin: 20px 5px;
`;

EventDetailForm.DisabledField = styled(Field)`
  & > div {
    background-color: #0049ff20;
    color: #000 !important;
  }
`;

EventDetailForm.Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 50px 0 15px;

  & > button {
    margin-left: 10px;
  }
`;

const mapStateToProps = createStructuredSelector({
  event: eventDataSelector,
  eventFormList: eventFormListSelector,
  eventObjects: eventObjectsDataSelector,
  eventTags: eventTagsDataSelector,
  formValues: state => {
    const valuesPath = ['form', 'eventDetailForm', 'values'];

    return state.hasIn(valuesPath) ? state.getIn(valuesPath).toJS() : {};
  },
  userData: userDataSelector,
  userStatus: userStatusSelector,
});

const mapDispatchToProps = {
  createEvent: eventActions.createEvent,
  setPersists: eventActions.setPersists,
  updateEvent: eventActions.updateEvent,
  updateEventTagObject: eventActions.updateEventTagObject,
};

const enhance = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withHandlers({
    hasEventRight: ({ userData }) => () => {
      return hasRight(userData.get('userrights').toJS(), RIGHTS.EVENTS.EVENT);
    },
    onChangeTagsObjects: ({
      eventID,
      eventTags,
      eventObjects,
      updateEventTagObject,
      formValues,
      eventFormList,
    }) => (tags, objectIDList) => {
      if (!eventID) {
        return;
      }
      const eventDetail = _.cloneDeep(formValues);
      const curEventForm = getEventForm(eventFormList, eventDetail.formID);
      if (
        curEventForm?.objectIDRequired &&
        (!objectIDList || objectIDList.length === 0)
      ) {
        return;
      }
      const payload = { tags, objectIDList };
      const unique = (value, index, self) => {
        return self.indexOf(value) === index;
      };
      if (!payload.tags) {
        payload.tags = eventTags.map(t => t.tag);
      }
      if (!payload.objectIDList) {
        payload.objectIDList = eventObjects.map(t => t.objectID);
      }

      // filter duplicate values
      payload.tags = payload.tags.filter(unique);
      payload.objectIDList = payload.objectIDList.filter(unique);

      updateEventTagObject({ eventID, payload });
    },
    onSubmit: ({
      eventID,
      event,
      eventFormList,
      createEvent,
      updateEvent,
      formValues,
      onCreateSuccess,
    }) => async () => {
      const eventDetail = _.cloneDeep(formValues);
      const curEventForm = getEventForm(eventFormList, eventDetail.formID);

      formatEventData(eventDetail, curEventForm.formFields);
      if (eventID) {
        await updateEvent({
          eventID,
          payload: {
            ...eventDetail,
            lastUpdate: event.updateDt,
          },
        });
      } else {
        const res = await createEvent({ payload: eventDetail });
        onCreateSuccess(res.results.event[0].eventID);
      }
    },
    onFreshCreate: ({ onCreateRedirect, setPersists }) => () => {
      setPersists({ eventData: {} });
      onCreateRedirect();
    },
  }),
  reduxForm({
    form: 'eventDetailForm',
    enableReinitialize: true,
    validate: (values, props) => {
      const formData = values.toJS();
      const error = {};

      if (!formData.formID) {
        error.formID = 'Required';
      }
      if (!formData.eventType) {
        error.eventType = 'Required';
      }
      const curEventForm = getEventForm(props.eventFormList, formData.formID);
      if (curEventForm?.objectIDRequired) {
        if (!formData.objectIDList || formData.objectIDList.length === 0) {
          error.objectIDList = 'Required';
        }
      }

      return error;
    },
  }),
  withHandlers({
    onClearForm: ({ initialize, setPersists }) => () => {
      setPersists({ eventData: {} });
      initialize({});
    },
    onDuplicate: ({
      pristine,
      onSubmit,
      eventFormList,
      formValues,
      setPersists,
      onCreateRedirect,
    }) => async () => {
      const curEventForm = getEventForm(eventFormList, formValues.formID);
      const persists = { eventData: {}, eventType: formValues.eventType };

      if (!pristine) {
        if (await confirm('Save changes before proceeding?')) {
          await onSubmit();
        }
      }

      curEventForm.formFields.forEach(field => {
        if (field.persist && formValues.eventData[field.name]) {
          persists.eventData[field.name] = formValues.eventData[field.name];
        }
      });

      setPersists(persists);
      onCreateRedirect();
    },
  }),
  lifecycle({
    componentWillUnmount() {
      this.props.setPersists({ eventData: {} });
    },
  }),
);

export default enhance(EventDetailForm);
